import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Map, { Marker, AttributionControl } from "react-map-gl";
import mapboxgl from "../../../utils/mapboxGlInit";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../ui/Card";
import { NPL_LOT_COORDS, NPL_MAP_CENTER } from "./nplLotMapConfig";
import {
  allocatedSubLotOccupancy,
  subLotCapacity,
} from "./nplOccupancyFromHourly";

const IDLE_COLOR = "#9ca3af";
/** Three equal fill bands — same as NationalParkOccupancyStrip. */
const FILL_GREEN = "#22c55e"; // < 43%
const FILL_YELLOW = "#facc15"; // 43–89%
const FILL_RED = "#ef4444"; // ≥ 90%

/** Even pixel sizes only — odd/fractional sizes blur under Mapbox marker transforms. */
const SIZE_STEPS = [32, 36, 40, 44, 48];

const FIT_PADDING = { top: 56, bottom: 88, left: 64, right: 64 };
const FIT_PADDING_MOBILE = { top: 48, bottom: 80, left: 56, right: 92 };
const FIT_MAX_ZOOM = 15.7;
const FIT_MAX_ZOOM_MOBILE = 15.05;

const HEBREW_TEXT_FIELD = [
  "coalesce",
  ["get", "name_he"],
  ["get", "name:he"],
  ["get", "name"],
  ["get", "name_en"],
];

function ensureRtlTextPlugin() {
  try {
    const status = mapboxgl.getRTLTextPluginStatus?.();
    if (status === "loaded" || status === "loading") return;
    mapboxgl.setRTLTextPlugin(
      "https://api.mapbox.com/mapbox-gl-js/plugins/mapbox-gl-rtl-text/v0.2.3/mapbox-gl-rtl-text.js",
      null,
      true
    );
  } catch (_) {
    // Already set / unavailable in this runtime.
  }
}

const STANDARD_DUSK_CONFIG = {
  basemap: {
    lightPreset: "dusk",
    showPointOfInterestLabels: false,
  },
};

function applyHebrewLanguage(map) {
  try {
    if (typeof map?.setLanguage === "function") {
      map.setLanguage("he");
    }
  } catch (_) {
    // Older GL JS builds without setLanguage.
  }
}

function applyStandardDusk(map) {
  if (typeof map?.setConfigProperty !== "function") return;
  try {
    map.setConfigProperty("basemap", "lightPreset", "dusk");
    map.setConfigProperty("basemap", "showPointOfInterestLabels", false);
  } catch (_) {
    // Classic styles do not expose Standard config properties.
  }
}

/** Prefer Hebrew / local OSM names on symbol layers (streets, places). */
function applyHebrewLabels(map) {
  if (!map?.getStyle) return;
  const style = map.getStyle();
  if (!style?.layers) return;

  style.layers.forEach((layer) => {
    if (layer.type !== "symbol") return;
    const textField = layer.layout && layer.layout["text-field"];
    if (textField == null) return;
    try {
      map.setLayoutProperty(layer.id, "text-field", HEBREW_TEXT_FIELD);
    } catch (_) {
      // Standard / some custom styles reject expression swaps — skip those layers.
    }
  });
}

/** Color by how full *this* lot is — green / yellow / red fill bands. */
function colorForOccupancyPct(occupancyPct) {
  if (occupancyPct >= 90) return FILL_RED;
  if (occupancyPct >= 43) return FILL_YELLOW;
  return FILL_GREEN;
}

function trafficVisual(occupied, total) {
  if (occupied <= 0 || total <= 0) {
    return { size: SIZE_STEPS[0], color: IDLE_COLOR, intensity: 0, occupancyPct: 0 };
  }
  const occupancyPct = Math.min(100, Math.round((occupied / total) * 100));
  const t = occupancyPct / 100;
  const eased = Math.sqrt(t);
  const step = Math.min(
    SIZE_STEPS.length - 1,
    Math.round(eased * (SIZE_STEPS.length - 1))
  );
  return {
    size: SIZE_STEPS[step],
    color: colorForOccupancyPct(occupancyPct),
    intensity: eased,
    occupancyPct,
  };
}

function formatCount(n) {
  const v = Math.max(0, Number(n) || 0);
  if (v >= 1000) {
    const k = v / 1000;
    return `${k >= 10 ? Math.round(k) : k.toFixed(1).replace(/\.0$/, "")}k`;
  }
  return String(v);
}

function normalizeVacancy(raw) {
  // "אלמ" = full lot (vacancy < 1 from API). Must be 0 free spaces —
  // returning null made the marker show 0 occupied while the strip showed 100%.
  if (raw === "אלמ") return 0;
  if (raw == null || raw === "" || Number.isNaN(Number(raw))) return null;
  return Math.max(0, Number(raw));
}

/** Absolute label position around the centered circle. */
function labelPlacementClass(placement = "bottom") {
  switch (placement) {
    case "top":
      return "bottom-full mb-1 left-1/2 -translate-x-1/2";
    case "left":
      return "right-full mr-1 top-1/2 -translate-y-1/2";
    case "right":
      return "left-full ml-1 top-1/2 -translate-y-1/2";
    case "bottom":
    default:
      return "top-full mt-1 left-1/2 -translate-x-1/2";
  }
}

/**
 * Flatten lot config into map markers.
 * CENTER_OF_THE_PARK expands to horse farm + volleyball pins (per-subLot stats).
 */
export function buildNplMapMarkers(deviceNames = []) {
  const order =
    deviceNames.length > 0 ? deviceNames : Object.keys(NPL_LOT_COORDS);
  const markers = [];

  order.forEach((lotKey, index) => {
    const cfg = NPL_LOT_COORDS[lotKey];
    if (!cfg) return;

    if (Array.isArray(cfg.subLocations) && cfg.subLocations.length > 0) {
      cfg.subLocations.forEach((sub) => {
        markers.push({
          id: `${lotKey}::${sub.id}`,
          lotKey,
          lotIndex: index,
          subLotKey: sub.subLotKey || null,
          capacity: Number(sub.capacity) || 0,
          labelHe: sub.labelHe,
          parentLabelHe: cfg.labelHe,
          lat: sub.lat,
          lng: sub.lng,
          labelPlacement: sub.labelPlacement || cfg.labelPlacement || "bottom",
        });
      });
      return;
    }

    markers.push({
      id: lotKey,
      lotKey,
      lotIndex: index,
      subLotKey: null,
      capacity: 0,
      labelHe: cfg.labelHe,
      parentLabelHe: null,
      lat: cfg.lat,
      lng: cfg.lng,
      labelPlacement: cfg.labelPlacement || "bottom",
    });
  });

  return markers;
}

function boundsFromMarkers(markers) {
  if (!markers?.length) return null;
  let minLat = Infinity;
  let maxLat = -Infinity;
  let minLng = Infinity;
  let maxLng = -Infinity;
  markers.forEach((m) => {
    minLat = Math.min(minLat, m.lat);
    maxLat = Math.max(maxLat, m.lat);
    minLng = Math.min(minLng, m.lng);
    maxLng = Math.max(maxLng, m.lng);
  });
  if (!Number.isFinite(minLat)) return null;
  if (minLat === maxLat) {
    minLat -= 0.0008;
    maxLat += 0.0008;
  }
  if (minLng === maxLng) {
    minLng -= 0.0008;
    maxLng += 0.0008;
  }
  return [
    [minLng, minLat],
    [maxLng, maxLat],
  ];
}

/**
 * National Park lot map — markers colored/sized by fill % of that lot's capacity
 * (same bands as the occupancy strip). Number inside = occupied cars.
 * Multi-pin lots (לב הפארק) show each sub-lot's own occupancy; strip stays combined.
 */
export function NationalParkSiteMap({
  config,
  deviceNames = [],
  totalParkingSpaces = [],
  vacancyValues = [],
  hourlyByLocation = {},
}) {
  const markers = useMemo(
    () => buildNplMapMarkers(deviceNames),
    [deviceNames]
  );
  const bounds = useMemo(() => boundsFromMarkers(markers), [markers]);

  const mapRef = useRef(null);
  const containerRef = useRef(null);

  const [viewState, setViewState] = useState({
    latitude: NPL_MAP_CENTER.lat,
    longitude: NPL_MAP_CENTER.lng,
    zoom: NPL_MAP_CENTER.zoom,
    bearing: 0,
    pitch: 0,
  });

  const fitToLots = useCallback(() => {
    const map = mapRef.current?.getMap?.() ?? mapRef.current;
    if (!map || !bounds) return;
    if (typeof map.resize === "function") map.resize();
    const narrow = (containerRef.current?.clientWidth || 800) < 520;
    map.fitBounds(bounds, {
      padding: narrow ? FIT_PADDING_MOBILE : FIT_PADDING,
      maxZoom: narrow ? FIT_MAX_ZOOM_MOBILE : FIT_MAX_ZOOM,
      duration: 0,
    });
  }, [bounds]);

  const onMapLoad = useCallback(
    (evt) => {
      ensureRtlTextPlugin();
      const map = evt?.target;
      if (map) {
        applyStandardDusk(map);
        applyHebrewLanguage(map);
        applyHebrewLabels(map);
        map.once?.("style.load", () => {
          applyHebrewLanguage(map);
          applyHebrewLabels(map);
        });
        map.once?.("idle", () => {
          applyStandardDusk(map);
          applyHebrewLanguage(map);
          applyHebrewLabels(map);
        });
      }
      fitToLots();
    },
    [fitToLots]
  );

  useEffect(() => {
    ensureRtlTextPlugin();
  }, []);

  useEffect(() => {
    fitToLots();
  }, [fitToLots, markers.length]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(() => {
      fitToLots();
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [fitToLots]);

  const mapConfig = config?.map || {};
  const MAPBOX_TOKEN = mapConfig.mapboxToken || process.env.REACT_APP_MAPBOX_TOKEN;
  const MAP_STYLE = "mapbox://styles/mapbox/standard";

  const occupancyByMarker = useMemo(() => {
    const result = {};
    const subAllocCache = {};

    markers.forEach((marker) => {
      if (marker.subLotKey) {
        if (!subAllocCache[marker.lotKey]) {
          const i = marker.lotIndex;
          const zoneTotal = Math.max(0, Number(totalParkingSpaces[i]) || 0);
          const zoneVacancy = normalizeVacancy(vacancyValues[i]);
          const zoneOccupied =
            zoneVacancy == null
              ? null
              : Math.max(0, zoneTotal - zoneVacancy);
          subAllocCache[marker.lotKey] = allocatedSubLotOccupancy(
            hourlyByLocation[marker.lotKey] || [],
            marker.lotKey,
            zoneOccupied
          );
        }
        const total =
          marker.capacity ||
          subLotCapacity(marker.lotKey, marker.subLotKey) ||
          0;
        const occupied = Math.max(
          0,
          Number(subAllocCache[marker.lotKey]?.[marker.subLotKey]) || 0
        );
        result[marker.id] = {
          occupied,
          total,
          vacancy: Math.max(0, total - occupied),
          ...trafficVisual(occupied, total),
        };
        return;
      }

      const i = marker.lotIndex;
      const total = Math.max(0, Number(totalParkingSpaces[i]) || 0);
      const vacancy = normalizeVacancy(vacancyValues[i]);
      const occupied =
        vacancy == null ? 0 : Math.max(0, total - vacancy);
      result[marker.id] = {
        occupied,
        total,
        vacancy: vacancy ?? 0,
        ...trafficVisual(occupied, total),
      };
    });

    return result;
  }, [
    markers,
    totalParkingSpaces,
    vacancyValues,
    hourlyByLocation,
  ]);

  if (!MAPBOX_TOKEN || !MAP_STYLE || markers.length === 0) {
    return (
      <Card className="h-full flex flex-col">
        <CardHeader className="border-b py-3 px-4">
          <CardTitle className="text-base font-bold text-gray-900 text-right">
            מפת חניונים
          </CardTitle>
          <CardDescription className="text-xs text-gray-500 text-right">
            מפת Mapbox אינה זמינה כרגע
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card className="h-full flex flex-col">
      <CardHeader className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between border-b py-3 px-4 min-h-0 sm:min-h-[73px] gap-2 sm:gap-3">
        <div
          dir="rtl"
          className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-gray-600"
        >
          <span className="inline-flex items-center gap-1.5">
            <span
              className="inline-block w-2.5 h-2.5 rounded-full"
              style={{ background: FILL_GREEN }}
            />
            פנוי יחסית
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span
              className="inline-block w-2.5 h-2.5 rounded-full"
              style={{ background: FILL_YELLOW }}
            />
            עמוס
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span
              className="inline-block w-2.5 h-2.5 rounded-full"
              style={{ background: FILL_RED }}
            />
            כמעט מלא
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span
              className="inline-block w-2.5 h-2.5 rounded-full"
              style={{ background: IDLE_COLOR }}
            />
            ריק
          </span>
        </div>
        <div className="text-right w-full sm:w-auto sm:shrink-0 min-w-0">
          <CardTitle className="text-base font-bold text-gray-900">
            מפת חניונים
          </CardTitle>
          <CardDescription className="text-xs text-gray-500 break-words">
            צבע = אחוז תפוסה · מספר = רכבים · לב הפארק: חווה+כדורעף = סה״כ בסרגל
          </CardDescription>
        </div>
      </CardHeader>
      <CardContent className="px-2 pt-2 sm:px-3 sm:pt-3 pb-3 flex-1">
        <div
          ref={containerRef}
          className="relative w-full h-[320px] sm:h-[360px] rounded-lg overflow-hidden border border-gray-200"
        >
          <Map
            ref={mapRef}
            {...viewState}
            onMove={(evt) => setViewState(evt.viewState)}
            attributionControl={false}
            interactive={false}
            dragPan={false}
            dragRotate={false}
            scrollZoom={false}
            boxZoom={false}
            touchZoom={false}
            touchRotate={false}
            touchPitch={false}
            keyboard={false}
            doubleClickZoom={false}
            cursor="default"
            style={{ width: "100%", height: "100%" }}
            mapStyle={MAP_STYLE}
            mapboxAccessToken={MAPBOX_TOKEN}
            mapLib={mapboxgl}
            language="he"
            config={STANDARD_DUSK_CONFIG}
            onLoad={onMapLoad}
          >
            <AttributionControl compact position="bottom-left" />
            {markers.map((marker) => {
              const stats = occupancyByMarker[marker.id] || {
                occupied: 0,
                size: SIZE_STEPS[0],
                color: IDLE_COLOR,
              };
              const size = stats.size;
              const countLabel = formatCount(stats.occupied);
              const titleParts = [
                marker.parentLabelHe
                  ? `${marker.parentLabelHe} · ${marker.labelHe}`
                  : marker.labelHe,
                `${countLabel} תפוסים`,
                stats.total ? `מתוך ${stats.total}` : null,
                stats.occupancyPct != null ? `${stats.occupancyPct}%` : null,
              ].filter(Boolean);

              return (
                <Marker
                  key={marker.id}
                  longitude={marker.lng}
                  latitude={marker.lat}
                  anchor="center"
                  // Avoid Mapbox re-creating marker DOM on every occupancy tick.
                  style={{ willChange: "auto" }}
                >
                  <div
                    title={titleParts.join(" — ")}
                    className="relative"
                    style={{
                      width: size,
                      height: size,
                      transform: "translateZ(0)",
                      backfaceVisibility: "hidden",
                    }}
                  >
                    <span
                      className="absolute inset-0 flex items-center justify-center rounded-full shadow-md leading-none"
                      style={{
                        backgroundColor: stats.color,
                        color: "#111827",
                        borderWidth: 2,
                        borderStyle: "solid",
                        borderColor: "#ffffff",
                        fontSize: 11,
                        fontWeight: 700,
                        WebkitFontSmoothing: "antialiased",
                        MozOsxFontSmoothing: "grayscale",
                      }}
                    >
                      {countLabel}
                    </span>
                    <span
                      className={`absolute z-10 px-1 py-0.5 rounded bg-white/90 border border-gray-200 text-[9px] font-semibold text-gray-800 shadow-sm whitespace-nowrap pointer-events-none ${labelPlacementClass(
                        marker.labelPlacement
                      )}`}
                      style={{ WebkitFontSmoothing: "antialiased" }}
                    >
                      {marker.labelHe}
                    </span>
                  </div>
                </Marker>
              );
            })}
          </Map>
        </div>
      </CardContent>
    </Card>
  );
}

export default NationalParkSiteMap;
